import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { PageHeader, TableWrap, Th, Td, Loading, EmptyState, Badge, exportCSV } from "../components/shared";
import { formatINR, formatDate } from "../lib/format";
import { TXN_TYPE_META, PAYMENT_MODES } from "../lib/constants";
import { useAuth } from "../context/AuthContext";
import { useActions } from "../context/ActionsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Search, Download, Undo2, Filter, Receipt as ReceiptIcon } from "lucide-react";
import { AttachmentViewer, AttachmentThumb } from "../components/AttachmentViewer";

export default function Transactions() {
  const [sp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: sp.get("search") || "", type: sp.get("type") || "", payment_mode: "",
    date_from: sp.get("date_from") || "", date_to: sp.get("date_to") || "",
  });
  const [reverse, setReverse] = useState(null);
  const [reason, setReason] = useState("");
  const [attView, setAttView] = useState(null);
  const { canWrite } = useAuth();
  const { openReceipt } = useActions();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams({ ...filters, page, limit: 50 });
      const { data } = await api.get(`/transactions?${q}`);
      setItems(data.items); setTotal(data.total);
    } finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("prabhat:refresh", h);
    return () => window.removeEventListener("prabhat:refresh", h);
  }, [load]);

  const doReverse = async () => {
    try {
      await api.post(`/transactions/${reverse.id}/reverse`, { reason });
      toast.success("Transaction reversed");
      setReverse(null); setReason(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doExport = () => exportCSV("transactions.csv", items.map((t) => ({
    TxnID: t.txn_number, Date: formatDate(t.txn_date), Type: TXN_TYPE_META[t.type]?.label || t.type,
    Party: t.candidate_name || t.payee_name || "", Description: t.description,
    Account: t.account_name || "", MoneyIn: t.money_in, MoneyOut: t.money_out || t.amount,
    Income: t.income_amount, Expense: t.expense_amount, Mode: t.payment_mode || "", By: t.created_by_name, Status: t.status,
  })));

  const set = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };

  return (
    <div>
      <PageHeader title="Transaction Center" subtitle={`${total} transactions`}
        actions={<Button size="sm" variant="outline" onClick={doExport} className="gap-1.5"><Download className="h-4 w-4" /> Export</Button>} />
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-3 mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="h-4 w-4 absolute left-3 top-2.5 text-slate-400" />
          <Input data-testid="txn-search" value={filters.search} onChange={(e) => set("search", e.target.value)} placeholder="Search txn no, receipt, reference" className="pl-9 h-9" />
        </div>
        <select data-testid="txn-type-filter" value={filters.type} onChange={(e) => set("type", e.target.value)} className="h-9 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 text-sm">
          <option value="">All Types</option>
          {Object.entries(TXN_TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select data-testid="txn-mode-filter" value={filters.payment_mode} onChange={(e) => set("payment_mode", e.target.value)} className="h-9 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2 text-sm">
          <option value="">All Modes</option>{PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <Input type="date" data-testid="txn-from" value={filters.date_from} onChange={(e) => set("date_from", e.target.value)} className="h-9 w-36" />
        <Input type="date" data-testid="txn-to" value={filters.date_to} onChange={(e) => set("date_to", e.target.value)} className="h-9 w-36" />
      </div>
      {loading ? <Loading /> : items.length === 0 ? <EmptyState title="No transactions match your filters" /> : (
        <TableWrap>
          <thead><tr>
            <Th>Txn ID</Th><Th>Date</Th><Th>Type</Th><Th>Party</Th><Th>Description</Th><Th>Account</Th>
            <Th className="text-right">Money In</Th><Th className="text-right">Money Out</Th><Th>Mode</Th><Th>By</Th><Th></Th>
          </tr></thead>
          <tbody>
            {items.map((t) => {
              const meta = TXN_TYPE_META[t.type] || { label: t.type, cls: "bg-slate-100 text-slate-700 border-slate-200" };
              const out = t.type === "transfer" ? t.amount : t.money_out;
              return (
                <tr key={t.id} data-testid={`txn-row-${t.id}`} className={`hover:bg-slate-50 dark:hover:bg-slate-800/40 ${t.reversed ? "opacity-60" : ""}`}>
                  <Td className="font-mono">{t.txn_number}{t.receipt_number ? <div className="text-[10px] text-slate-400">{t.receipt_number}</div> : null}</Td>
                  <Td>{formatDate(t.txn_date)}</Td>
                  <Td><Badge cls={meta.cls}>{meta.label}</Badge></Td>
                  <Td className="font-medium">{t.candidate_name || t.payee_name || (t.type === "transfer" ? "Internal" : "-")}</Td>
                  <Td className="max-w-[200px] truncate">{t.description || "-"}</Td>
                  <Td>{t.account_name || (t.type === "transfer" ? "Transfer" : "-")}</Td>
                  <Td className="text-right font-mono text-emerald-600">{t.money_in ? formatINR(t.money_in) : "-"}</Td>
                  <Td className="text-right font-mono text-rose-600">{out ? formatINR(out) : "-"}</Td>
                  <Td>{t.payment_mode || "-"}</Td>
                  <Td className="text-slate-400">{t.created_by_name}</Td>
                  <Td><div className="flex items-center gap-1.5">
                    {["income", "candidate_advance"].includes(t.type) && t.receipt_number && (
                      <button data-testid={`receipt-${t.id}`} onClick={() => openReceipt(t.id)} className="text-teal-600 hover:text-teal-800" title="View receipt"><ReceiptIcon className="h-3.5 w-3.5" /></button>
                    )}
                    {t.attachment_ids?.length > 0 && (
                      <AttachmentThumb ids={t.attachment_ids} testid={`attach-${t.id}`} onClick={() => setAttView(t.attachment_ids)} />
                    )}
                    {canWrite && t.status === "posted" && !t.reversed && !t.reversal_of && t.type !== "income_recognition" && (
                      <button data-testid={`reverse-${t.id}`} onClick={() => setReverse(t)} className="text-rose-500 hover:text-rose-700" title="Reverse"><Undo2 className="h-3.5 w-3.5" /></button>
                    )}
                    {t.reversed && <Badge cls="bg-rose-50 text-rose-600 border-rose-200">Reversed</Badge>}
                    {t.status === "pending_approval" && <Badge cls="bg-amber-50 text-amber-700 border-amber-200">Pending</Badge>}
                    {t.status === "rejected" && <Badge cls="bg-rose-50 text-rose-600 border-rose-200">Rejected</Badge>}
                  </div></Td>
                </tr>
              );
            })}
          </tbody>
        </TableWrap>
      )}
      {total > 50 && (
        <div className="flex items-center justify-between mt-3 text-sm">
          <span className="text-slate-400">Page {page} of {Math.ceil(total / 50)}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
            <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / 50)} onClick={() => setPage((p) => p + 1)}>Next</Button>
          </div>
        </div>
      )}
      <Dialog open={!!reverse} onOpenChange={(o) => !o && setReverse(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reverse Transaction {reverse?.txn_number}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">A linked reversal entry will be created. The original is preserved for audit.</p>
          <Textarea data-testid="reverse-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for reversal (required)" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverse(null)}>Cancel</Button>
            <Button variant="destructive" disabled={!reason} onClick={doReverse} data-testid="reverse-confirm">Reverse</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AttachmentViewer ids={attView || []} open={!!attView} onClose={() => setAttView(null)} />
    </div>
  );
}
